import React, { useCallback, useEffect, useRef, useState } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import clsx from "clsx";
import {
  DocumentUploadPreviewDialog,
  DOCUMENT_UPLOAD_PREVIEW_Z_INDEX,
} from "./DocumentUploadPreviewDialog";
import {
  getDocumentUploadDestinationLabel,
  useDocumentUploadQueueOptional,
} from "../../../contexts/document-upload-queue-context";
import { useDocumentUploadPreviewRenderers } from "./useDocumentUploadPreviewRenderers";
import {
  consumeDocumentPlacementRestore,
  getDocumentPlacementSession,
  subscribeDocumentPlacement,
} from "../drawings/drawingDocumentPlacementSession";
import { prepareUploadEntries } from "./documentUploadDrawingUtils";

const IMAGE_ACCEPT =
  "image/png,image/jpeg,image/jpg,image/gif,image/svg,image/svg+xml,image/webp";

function revokePreviewUrls(files) {
  files.forEach((entry) => {
    if (entry.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(entry.previewUrl);
    }
  });
}

function buildPendingEntries(imageFiles, module, moduleParam) {
  return imageFiles.map((file) => ({
    file,
    previewUrl: URL.createObjectURL(file),
    title: file.name.replace(/\.[^/.]+$/, ""),
    module,
    moduleParam,
    folder_id: null,
    drawing_id: null,
    drawing_position_x: null,
    drawing_position_y: null,
    drawing_rotation: 0,
    want_sketch: false,
  }));
}

/**
 * Direct image upload — preview dialog, then global background upload queue.
 * Pass `previewOptions` to enable folder / drawing extras (report, project).
 */
export function DirectImageUploadButton({
  module,
  moduleParam,
  disabled = false,
  subtitle,
  className,
  zIndex = DOCUMENT_UPLOAD_PREVIEW_Z_INDEX,
  size = "compact",
  previewOptions,
  /** Optional ref object; `.current` is set to open the file picker programmatically */
  openPickerRef,
  onUploadComplete,
}) {
  const { t } = useTranslation();
  const uploadQueue = useDocumentUploadQueueOptional();
  const inputRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const restoredRef = useRef(false);

  const handlePlacementStarted = useCallback(() => {
    // Hide preview while placing on the full Tegninger canvas.
    // Allow subsequent restore after a new placement starts.
    restoredRef.current = false;
    setPreviewOpen(false);
  }, []);

  const previewRenderers = useDocumentUploadPreviewRenderers({
    previewOptions,
    filesToUpload: pendingFiles,
    setFilesToUpload: setPendingFiles,
    module,
    moduleParam,
    onPlacementStarted: handlePlacementStarted,
  });

  const clearPending = useCallback(() => {
    setPendingFiles((current) => {
      revokePreviewUrls(current);
      return [];
    });
  }, []);

  const pendingRef = useRef(pendingFiles);
  pendingRef.current = pendingFiles;

  useEffect(() => {
    // Do not revoke preview URLs on unmount if an active placement session
    // for this module/param might still be using the same entries.
    return () => {
      const session = getDocumentPlacementSession();
      const shouldSkipRevoke =
        session &&
        (!session.source || session.source === "direct") &&
        String(session.module) === String(module) &&
        String(session.moduleParam) === String(moduleParam);
      if (!shouldSkipRevoke) {
        revokePreviewUrls(pendingRef.current);
      }
    };
  }, [module, moduleParam]);

  // Restore upload preview after full-canvas pin placement (mount + session complete).
  useEffect(() => {
    const tryRestore = () => {
      if (restoredRef.current || !module || moduleParam == null) return;
      const peek = getDocumentPlacementSession();
      if (!peek || (peek.source && peek.source !== "direct")) return;
      if (peek.status !== "completed" && peek.status !== "cancelled") return;
      if (
        String(peek.module) !== String(module) ||
        String(peek.moduleParam) !== String(moduleParam)
      ) {
        return;
      }
      const session = consumeDocumentPlacementRestore(module, moduleParam);
      if (!session?.files?.length) return;
      restoredRef.current = true;
      setPendingFiles(session.files);
      // Reopen preview after place or cancel so the user can continue/adjust.
      if (session.status === "completed" || session.status === "cancelled") {
        setPreviewOpen(true);
      }
    };
    tryRestore();
    return subscribeDocumentPlacement(tryRestore);
  }, [module, moduleParam]);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const canRender = Boolean(uploadQueue && !disabled && module && moduleParam);

  useEffect(() => {
    if (!openPickerRef) return undefined;
    openPickerRef.current = canRender ? openPicker : null;
    return () => {
      openPickerRef.current = null;
    };
  }, [openPickerRef, openPicker, canRender]);

  if (!canRender) {
    return null;
  }

  const destinationLabelSubtitle =
    subtitle ||
    getDocumentUploadDestinationLabel(module, moduleParam, null, t);

  const appendSelectedFiles = (selected) => {
    const imageFiles = selected.filter((file) => file.type?.startsWith("image/"));
    if (!imageFiles.length) {
      toast.error(t("documentUpload.direct.imagesOnly"));
      return;
    }

    const entries = previewRenderers.enhancePendingEntries(
      buildPendingEntries(imageFiles, module, moduleParam)
    );
    setPendingFiles((prev) => [...prev, ...entries]);
    setPreviewOpen(true);
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) {
      return;
    }
    appendSelectedFiles(selected);
  };

  const handleDeleteFile = (index) => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setPreviewOpen(false);
      }
      return next;
    });
  };

  const handleTitleChange = (index, value) => {
    setPendingFiles((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, title: value } : entry))
    );
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    clearPending();
  };

  const handleConfirmUpload = () => {
    if (!pendingFiles.length) {
      return;
    }

    const destinationLabel = getDocumentUploadDestinationLabel(
      module,
      moduleParam,
      destinationLabelSubtitle,
      t
    );

    // Strip drawing_id without coords so complete_upload does not 422
    // (cancelled place / multi-file leftovers).
    const toUpload = prepareUploadEntries(pendingFiles);
    if (!toUpload.length) {
      toast.error(t("documentUpload.direct.imagesOnly"));
      return;
    }

    uploadQueue.enqueueUpload({
      files: toUpload,
      destinationLabel,
      module,
      moduleParam,
      onComplete: (uploadedDocuments) => {
        const docs = (uploadedDocuments || []).filter((d) => d?.id != null);
        onUploadComplete?.(docs);
      },
    });
    uploadQueue.minimizeBar();
    revokePreviewUrls(toUpload);

    setPreviewOpen(false);
    setPendingFiles([]);
  };

  const isLarge = size === "large";

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        className={clsx(
          "inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border-2 border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm transition-colors duration-200 hover:border-emerald-600 hover:bg-emerald-100 active:bg-emerald-200 touch-manipulation",
          isLarge
            ? "min-h-[2.75rem] w-full justify-center px-4 py-3 sm:w-auto"
            : "min-h-[2.25rem] px-2.5 py-1.5 sm:min-h-[2rem] sm:px-2 sm:py-1",
          className
        )}
        title={t("documentUpload.direct.tooltip")}
        aria-label={t("documentUpload.direct.tooltip")}
      >
        <CameraIcon
          className={clsx(
            "flex-shrink-0 text-emerald-700",
            isLarge ? "h-5 w-5" : "h-5 w-5 sm:h-4 sm:w-4"
          )}
          aria-hidden
        />
        <span
          className={clsx(
            "font-bold leading-tight",
            isLarge ? "text-sm" : "max-w-[5.5rem] truncate text-[11px] sm:max-w-none sm:text-xs"
          )}
        >
          {t("documentUpload.direct.buttonShort")}
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={handleFileChange}
      />

      <DocumentUploadPreviewDialog
        open={previewOpen && pendingFiles.length > 0}
        onClose={handleClosePreview}
        filesToUpload={pendingFiles}
        onTitleChange={handleTitleChange}
        onDeleteFile={handleDeleteFile}
        onAddMore={openPicker}
        onConfirm={handleConfirmUpload}
        supportPdf={false}
        zIndex={zIndex}
        renderBeforeList={previewRenderers.renderBeforeList}
        renderFileExtra={previewRenderers.renderFileExtra}
        renderAfterList={previewRenderers.renderAfterList}
      />
    </>
  );
}
